import Focus from '../../../../shared/types/enums/focus';
import Key from '../../../../shared/classes/key';
import VirtualKeyboardContext from '../../../../shared/components/utils/context/virtualKeyboardContext';
import mosaicKeyboardHandler from '../../../../shared/components/utils/helpers/mosaicKeyboard';
import useKeyboard from '../../../../shared/components/utils/hooks/useKeyboard';
import VIRTUAL_KEYBOARD_ACTIONS from '../../../../shared/types/enums/virtualKeyboardActions';
import { useAppSelector } from '../../../../shared/store/hooks';
import { selectFocus } from '../../../../shared/store/slices/focus.slice';
import { useMemo, useRef, useState, useCallback } from 'react';

function VirtualKeyboard({
  focus,
  keys,
  maxCols,
  onChange,
  onDelete,
  outOfBounds,
}: {
  focus: Focus;
  keys: Key[];
  maxCols: number;
  onChange: (char: string) => void;
  onDelete: (pressed?: boolean) => void;
  outOfBounds: (focus: Focus, key: string) => void;
}) {
  const focusSlice = useAppSelector(selectFocus);
  const [index, setIndex] = useState(0);
  const indexRef = useRef<number>(0);
  const totalKeys = useMemo(
    () =>
      keys.reduce((value, key) => {
        return value + ((key.spaces as number[])?.length || 1);
      }, 0),
    []
  );

  // Refs

  indexRef.current = index;

  // Actions

  const fetchKey = useCallback((index?: number) => {
    const item = keys.find((key) => {
      return (
        ((key.spaces as number[]).length &&
          (key.spaces as number[]).includes(index || indexRef.current)) ||
        (index ?? indexRef.current) === key.spaces
      );
    });
    return item;
  }, []);

  const triggerKey = useCallback((key?: Key, pressed?: boolean) => {
    if (key) {
      switch (key?.action) {
        case VIRTUAL_KEYBOARD_ACTIONS.CUSTOM:
          key?.customAction && key.customAction();
          break;
        case VIRTUAL_KEYBOARD_ACTIONS.DELETE:
          onDelete(pressed);
          break;
        case VIRTUAL_KEYBOARD_ACTIONS.WRITE:
          onChange(key.component.props.char);
          break;
      }
    }
  }, []);

  // Event handlers

  const onClick = useCallback((index?: number) => {
    if (index === undefined) return;
    const item = fetchKey(index);
    triggerKey(item);
  }, []);

  // Keyboard hook

  useKeyboard({
    focus: focus,
    keyDownHandler: (key, pressed) => {
      const item = fetchKey();
      if (
        item?.jump &&
        item.jump[key as 'LEFT' | 'RIGHT' | 'UP' | 'DOWN'] <= -1 &&
        key !== 'SELECT'
      ) {
        outOfBounds(focus, key);
        return;
      }
      mosaicKeyboardHandler({
        indexRef,
        key,
        length: totalKeys,
        maxCols,
        setIndex,
      });

      if (item?.component.props.index !== indexRef.current) {
        if (item?.jump && item?.jump[key as 'LEFT' | 'RIGHT' | 'UP' | 'DOWN']) {
          setIndex(
            item?.jump[key as 'LEFT' | 'RIGHT' | 'UP' | 'DOWN'] ||
              indexRef.current
          );
        }
      }
      if (key === 'SELECT') {
        triggerKey(item, pressed);
      }
    },
  });

  const contextValue = {
    listIndex: index,
    listFocused: focus === focusSlice,
    onClick,
  };

  return (
    <VirtualKeyboardContext.Provider value={contextValue}>
      {keys.map((key) => {
        const Component = key.component;
        return Component;
      })}
    </VirtualKeyboardContext.Provider>
  );
}

export default VirtualKeyboard;
